/**
 * Wait for Clerk's browser session to become usable by the server.
 *
 * Clerk reports a signed-in client state before the short-lived __session
 * cookie is necessarily visible to a following document request. The caller
 * supplies a same-origin server probe so this boundary is verified from the
 * server's point of view rather than inferred from client state alone.
 */
export async function waitForAuthSession(options: {
  getToken: (options: { skipCache: true }) => Promise<string | null>
  probe: () => Promise<boolean>
  timeoutMs?: number
  intervalMs?: number
}): Promise<boolean> {
  const timeoutMs = options.timeoutMs ?? 5000
  const deadline = Date.now() + timeoutMs
  const token = await withTimeout(
    options.getToken({ skipCache: true }),
    Math.max(0, deadline - Date.now()),
  )
  if (!token) {
    return false
  }

  const intervalMs = options.intervalMs ?? 100

  while (true) {
    const remainingMs = deadline - Date.now()
    if (remainingMs <= 0) {
      return false
    }

    // Bound the probe too. A mobile browser can leave a fetch pending while
    // its network changes, and that must become the visible retry state.
    if (await withTimeout(options.probe(), remainingMs)) {
      return true
    }

    const remainingAfterProbeMs = deadline - Date.now()
    if (remainingAfterProbeMs <= 0) {
      return false
    }
    await new Promise<void>((resolve) => {
      globalThis.setTimeout(resolve, Math.min(intervalMs, remainingAfterProbeMs))
    })
  }
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = globalThis.setTimeout(() => resolve(undefined), timeoutMs)
    void promise.then(
      (value) => {
        globalThis.clearTimeout(timer)
        resolve(value)
      },
      () => {
        globalThis.clearTimeout(timer)
        resolve(undefined)
      },
    )
  })
}

/**
 * Run the client-to-server auth handoff with an explicit cancellation boundary.
 * The React caller can safely re-render into its waiting state without
 * canceling this promise; only the effect cleanup should call the returned
 * function.
 */
export function startAuthSessionHandoff(options: {
  waitForSession: () => Promise<boolean>
  onReady: () => void
  onFailed: () => void
}): () => void {
  let cancelled = false

  void options.waitForSession()
    .then((ready) => {
      if (cancelled) return
      if (ready) {
        options.onReady()
      } else {
        options.onFailed()
      }
    })
    .catch(() => {
      if (!cancelled) options.onFailed()
    })

  return () => {
    cancelled = true
  }
}
